import type { PasswordChangeSchema } from '@repo/schemas/auth/passwordChange';
import type { UserPrivate } from '@repo/types/users';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { passwordChangeSchema } from '@repo/schemas/auth/passwordChange';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';

import { useSetCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

export const usePasswordChange = (mutateAsync: UseMutateAsyncFunction<UserPrivate, Error, PasswordChangeSchema, unknown>) => {
  const navigate = useNavigate();
  const setCredentials = useSetCredentials();
  const [searchParams] = useSearchParams();
  const authToken: string | undefined = searchParams.get('t') ?? undefined;

  const form = useForm<PasswordChangeSchema>({
    defaultValues: {
      password: '',
      confirmPassword: '',
      authToken: authToken,
    },
    mode: 'onSubmit',
    resolver: standardSchemaResolver(passwordChangeSchema),
  });

  const onSubmit = async (data: PasswordChangeSchema) => {
    mutateAsync(data)
      .then((user) => {
        if (authToken) {
          setCredentials({ ...user, avatarURL: getAvatar(user.avatarURL) ?? null });
          navigate('/');
        } else {
          navigate('/settings');
        }
      })
      .catch((error) => {
        const errorHandler = new ErrorMessageHandler(baseErrorMessages);
        const { detail } = errorHandler.processError(error);
        form.setError('password', { type: 'manual', message: detail });
        form.setError('confirmPassword', { type: 'manual', message: detail });
      });
  };

  return {
    form: form,
    onSubmit: onSubmit,
  };
};
